import { motion } from "framer-motion";
import { fadeUp, stagger, viewportOnce } from "../../lib/motion";
import { SectionHeading } from "./SectionHeading";
import { useLang } from "../../i18n/LanguageContext";

const NODES = [
  { id: "us1", x: 235, y: 205 },
  { id: "us2", x: 205, y: 220 },
  { id: "ca", x: 220, y: 165 },
  { id: "es", x: 470, y: 190 },
  { id: "in", x: 700, y: 250 },
];
const HUB = { x: 700, y: 250 };

const arc = (a, b) => {
  const mx = (a.x + b.x) / 2;
  const my = Math.min(a.y, b.y) - 60;
  return `M ${a.x} ${a.y} Q ${mx} ${my} ${b.x} ${b.y}`;
};

const WorldMap = ({ patiala }) => (
  <svg viewBox="0 0 1000 500" className="h-full w-full" fill="none" preserveAspectRatio="xMidYMid slice">
    <defs>
      <radialGradient id="node" cx="50%" cy="50%" r="50%">
        <stop offset="0%" stopColor="#FDE68A" />
        <stop offset="100%" stopColor="#D97706" />
      </radialGradient>
      <pattern id="dots" width="14" height="14" patternUnits="userSpaceOnUse">
        <circle cx="1.4" cy="1.4" r="1.4" fill="rgba(148,163,184,0.16)" />
      </pattern>
    </defs>
    <g fill="url(#dots)">
      <path d="M120 120 Q200 90 300 130 Q340 200 280 260 Q200 300 150 250 Q100 190 120 120Z" />
      <path d="M420 130 Q520 110 560 170 Q540 240 470 250 Q420 210 420 130Z" />
      <path d="M600 160 Q760 130 820 210 Q840 300 740 330 Q640 320 610 250 Q590 200 600 160Z" />
      <path d="M470 300 Q540 290 560 360 Q520 430 460 410 Q440 350 470 300Z" />
    </g>
    {NODES.map((n, i) => (
      <motion.path
        key={n.id}
        d={arc(n, HUB)}
        stroke="url(#node)"
        strokeWidth="1.6"
        strokeLinecap="round"
        initial={{ pathLength: 0, opacity: 0 }}
        whileInView={{ pathLength: 1, opacity: 0.9 }}
        viewport={{ once: true }}
        transition={{ duration: 1.6, delay: 0.3 + i * 0.25, ease: "easeInOut" }}
      />
    ))}
    {NODES.map((n, i) => (
      <motion.circle
        key={`p-${n.id}`}
        r="3.4"
        fill="#FDE68A"
        style={{ offsetPath: `path("${arc(n, HUB)}")` }}
        animate={{ offsetDistance: ["0%", "100%"] }}
        transition={{ duration: 3, repeat: Infinity, delay: i * 0.6, ease: "easeInOut" }}
      />
    ))}
    {[...NODES, HUB].map((n, i) => (
      <g key={`n-${i}`}>
        <circle cx={n.x} cy={n.y} r="9" fill="rgba(245,158,11,0.18)">
          <animate attributeName="r" values="6;13;6" dur="2.6s" repeatCount="indefinite" begin={`${i * 0.3}s`} />
        </circle>
        <circle cx={n.x} cy={n.y} r="4" fill="url(#node)" />
      </g>
    ))}
    <text x={HUB.x + 14} y={HUB.y + 4} fill="#FDE68A" fontSize="15" fontFamily="Outfit" letterSpacing="1">
      {patiala}
    </text>
  </svg>
);

export const GlobalInspiration = () => {
  const { t } = useLang();
  const s = t.inspiration;
  return (
    <section id="inspiration" data-testid="global-inspiration-section" className="relative overflow-hidden bg-ink-800 py-24 md:py-32 grain">
      <div className="pointer-events-none absolute inset-0 opacity-40">
        <div className="absolute left-0 top-1/2 h-full w-full -translate-y-1/2">
          <WorldMap patiala={s.patiala} />
        </div>
      </div>
      <div className="absolute inset-0 bg-gradient-to-b from-ink-800 via-ink-800/50 to-ink-800" />

      <div className="relative z-10 mx-auto max-w-7xl px-6 md:px-8">
        <SectionHeading eyebrow={s.eyebrow} title={s.title} accent={s.accent} subtitle={s.subtitle} testid="inspiration-heading" />

        <motion.div variants={stagger} initial="hidden" whileInView="visible" viewport={viewportOnce} className="mt-16 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {s.festivals.map((f) => (
            <motion.article
              key={f.short}
              variants={fadeUp}
              data-testid={`festival-card-${f.short.toLowerCase().replace(/\s+/g, "-")}`}
              className="group relative flex flex-col rounded-2xl glass p-7 transition-colors duration-300 hover:border-gold-light/50"
            >
              <div className="mb-5 flex items-center justify-between">
                <span className="text-4xl" aria-hidden="true">{f.flag}</span>
                <span className="rounded-full border border-white/15 px-3 py-1 text-[10px] uppercase tracking-[0.2em] text-white/60">{f.country}</span>
              </div>
              <h3 className="font-display text-2xl font-medium leading-snug text-white">{f.title}</h3>
              <p className="mt-4 flex-1 text-sm leading-relaxed text-white/65">{f.blurb}</p>
              <div className="mt-6 flex items-center justify-between border-t border-white/10 pt-4">
                <span className="text-xs uppercase tracking-[0.2em] text-gold-light">{f.short}</span>
                <span className="text-xs text-white/40">{s.sourceLabel}: {f.source}</span>
              </div>
              <div className="pointer-events-none absolute -inset-px rounded-2xl opacity-0 transition-opacity duration-500 group-hover:opacity-100 [background:radial-gradient(400px_circle_at_center,_rgba(245,158,11,0.08),transparent)]" />
            </motion.article>
          ))}
        </motion.div>

        <p className="mt-10 text-center text-xs text-white/40">{s.disclaimer}</p>
      </div>
    </section>
  );
};
